import { fetchWithAuth } from '../utils/api';
import { BILL_PRESETS } from './billTemplates';

/**
 * One shared copy of the shop's saved bill designs.
 *
 * Four separate screens need this list — the till, the bill on screen, the Bill
 * Format editor and the Branches board — and each used to fetch it on mount.
 * Opening the till fired the same request several times over, and because an
 * expired access token makes every request 401 once before the refresh retries
 * it, each of those appeared twice in the console. The list changes only when an
 * admin edits a design, so it is fetched once and shared.
 *
 * A failed load is not an error worth surfacing: the built-in presets need no
 * fetch, so a till with no network still prints.
 */

let cache = null;        // the last successful list
let inFlight = null;     // the request everyone joins while it is running

/** Everyone waiting on the same request gets the same array. */
export const loadBillTemplates = () => {
  if (cache) return Promise.resolve(cache);
  if (inFlight) return inFlight;

  inFlight = fetchWithAuth('/bill-templates')
    .then((res) => (res.ok ? res.json() : []))
    .then((list) => {
      const serverList = Array.isArray(list) ? list : [];
      const existingNames = new Set(serverList.map((t) => t.name));
      const missingPresets = BILL_PRESETS.filter((p) => !existingNames.has(p.name));
      cache = [...serverList, ...missingPresets];
      return cache;
    })
    .catch(() => BILL_PRESETS)
    .finally(() => { inFlight = null; });

  return inFlight;
};

/**
 * Replace the shared copy after an edit, so the other screens do not have to
 * refetch to see it. Called by the editor, which already has the new list.
 */
export const setBillTemplates = (list) => {
  cache = Array.isArray(list) ? list : [];
};

/** Forget the shared copy — used on sign-out, when it belongs to another shop. */
export const clearBillTemplates = () => {
  cache = null;
  inFlight = null;
};

/**
 * Write the starter designs into this organization, once.
 *
 * They used to live only in the frontend, so a shop saw designs with a button
 * it could not press — there was no row to edit. The server holds the flag that
 * makes this a no-op the second time, and `force` is what the "Restore
 * ready-made designs" button sends when a shop wants a deleted one back.
 *
 * The list is sent from here rather than kept in Go as well: two copies of the
 * same designs, in two languages, would drift.
 */
export const seedBillTemplates = async ({ force = false } = {}) => {
  const res = await fetchWithAuth('/bill-templates/seed', {
    method: 'POST',
    body: JSON.stringify({
      force,
      templates: BILL_PRESETS.map((p) => ({
        presetId: p.id,
        name: p.name,
        settings: p.settings,
      })),
    }),
  });
  if (!res.ok) throw new Error('Could not add the ready-made designs');
  const list = await res.json();
  cache = Array.isArray(list) ? list : [];
  return cache;
};
